import { memo, useState } from 'react';
import { Button, Col, Row, Alert } from 'react-bootstrap';
import { useApp } from '../../../../hook/app.hook';
import { Question, Section } from '../../../../response/test.response';
import OverviewComponent from '../overview.component';

const JeeActionComponent = () => {
  const {
    testRes,
    moveNext,
    movePrevious,
    markQuestionForRevisit,
    markQuestionAsSaved,
    clearQuestionResponse,
    updateActiveSection,
    updateActiveQuestion,
  } = useApp();

  const [showAlert, setShowAlert] = useState(false);

  const section = testRes.sectionList.filter(
    (section: Section) => section.active,
  )[0];

  const question = section.questionList.filter(
    (question: Question) => question.active,
  )[0];

  const activeQuestionIndex = question.index;
  const currentSectionIndex = testRes.sectionList.findIndex(
    (section: Section) => section.active,
  );

  const handleSaveAndNext = () => {
    if (!question.attempted) {
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 5000);
      return;
    }
    // Mark question as saved when using SAVE & NEXT
    markQuestionAsSaved(question.id);
    if (question.markedForRevisit) {
      markQuestionForRevisit(question.id);
    }
    moveNextQuestion();
  };

  const moveNextQuestion = () => {
    if (activeQuestionIndex < section.questionList.length) {
      moveNext();
    } else {
      if (currentSectionIndex < testRes.sectionList.length - 1) {
        const nextSection = testRes.sectionList[currentSectionIndex + 1];
        updateActiveSection(nextSection.id);
        updateActiveQuestion(nextSection.questionList[0].id);
      } else {
        const firstSection = testRes.sectionList[0];
        updateActiveSection(firstSection.id);
        updateActiveQuestion(firstSection.questionList[0].id);
      }
    }
  };

  const handleNext = () => {
    // If question is not saved (via SAVE & NEXT), clear it before moving
    // If question is already saved, keep it saved
    if (!question.saved) {
      clearQuestionResponse(question.id);
    }
    moveNextQuestion();
  };

  const handleClear = () => {
    clearQuestionResponse(question.id);
  };

  const handleSaveAndMarkForReview = () => {
    if (!question.attempted) {
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 5000);
      return;
    }
    if (!question.markedForRevisit) {
      markQuestionForRevisit(question.id);
    }
    markQuestionAsSaved(question.id);
    moveNextQuestion();
  };

  const handleMarkForReviewAndNext = () => {
    if (!question.markedForRevisit) {
      markQuestionForRevisit(question.id);
    }
    // If question is not saved (via SAVE & NEXT), clear it before moving
    // If question is already saved, keep it saved
    if (!question.saved) {
      clearQuestionResponse(question.id);
    }
    moveNextQuestion();
  };

  const handleBack = () => {
    if (activeQuestionIndex > 1) {
      movePrevious();
    } else {
      if (currentSectionIndex > 0) {
        const previousSection = testRes.sectionList[currentSectionIndex - 1];
        updateActiveSection(previousSection.id);
        updateActiveQuestion(
          previousSection.questionList[previousSection.questionList.length - 1]
            .id,
        );
      } else {
        const lastSection = testRes.sectionList[testRes.sectionList.length - 1];
        updateActiveSection(lastSection.id);
        updateActiveQuestion(
          lastSection.questionList[lastSection.questionList.length - 1].id,
        );
      }
    }
  };

  return (
    <>
      {/* 🔔 Alert */}
      {showAlert && (
        <Alert
          variant="warning"
          dismissible
          onClose={() => setShowAlert(false)}
          style={{
            position: 'absolute',
            top: '8px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1050,
            minWidth: '420px',
          }}
        >
          Please attempt the question before proceeding.
        </Alert>
      )}

      <div className="px-0 py-2 jee-action-buttons">
        <div className="border-top border-lightgray pt-2">
          <Row className="mb-2">
            <Col xs={12} md={2}>
              <Button
                variant="success"
                size="sm"
                className="font-weight-700 py-2 text-white border-0 rounded-0"
                onClick={handleSaveAndNext}
                style={{ fontSize: '13px', fontWeight: 600 }}
              >
                SAVE & NEXT
              </Button>
            </Col>

            <Col xs={12} md={2}>
              <Button
                variant="light"
                className="font-weight-500 py-2 text-dark border border-secondary rounded-0"
                onClick={handleClear}
                disabled={!question.attempted}
                style={{ fontSize: '13px', fontWeight: 600 }}
              >
                CLEAR
              </Button>
            </Col>

            <Col xs={12} md={4}>
              <Button
                variant="warning"
                size="sm"
                className="font-weight-500 py-2 text-white border-0 rounded-0"
                onClick={handleSaveAndMarkForReview}
                style={{ fontSize: '13px', fontWeight: 600 }}
              >
                SAVE & MARK FOR REVIEW
              </Button>
            </Col>

            <Col xs={12} md={4}>
              <Button
                variant="primary"
                size="sm"
                className="font-weight-500 py-2 text-white border-0 rounded-0"
                onClick={handleMarkForReviewAndNext}
                style={{ fontSize: '13px', fontWeight: 600 }}
              >
                MARK FOR REVIEW & NEXT
              </Button>
            </Col>
          </Row>

          <div className="border border-secondary rounded px-2 d-flex align-items-center justify-content-between">
            <div className="d-flex gap-2">
              <Button
                size="sm"
                variant="light"
                onClick={handleBack}
                style={{ fontSize: '13px', fontWeight: 600 }}
              >
                &lt;&lt; BACK
              </Button>
              <Button
                size="sm"
                variant="light"
                onClick={handleNext}
                style={{ fontSize: '13px', fontWeight: 600 }}
              >
                NEXT &gt;&gt;
              </Button>
            </div>
            <OverviewComponent />
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(JeeActionComponent);
